import {
  VideoPreset,
  type ScreenShareCaptureOptions,
  type TrackPublishDefaults,
  type VideoEncoding,
  type VideoResolution,
} from 'livekit-client';

/**
 * Perfis de qualidade da tela (screen share).
 *
 * - "normal" (default) = captura até 1080p (nativo, sem downscale de tela Full HD) @ 15 fps,
 *   ≤1,5 Mbps, 1 camada. Equivale ao `ScreenSharePresets.h720fps15` do livekit-client em
 *   bitrate, mas sem rebaixar a resolução — texto pequeno continua legível.
 * - "high" (opt-in) = 1080p @ 15 fps, ≤2,5 Mbps (`ScreenSharePresets.h1080fps15`).
 *
 * Histórico (set/2026 — "tela embaçada com sinal bom"): o Normal capturava 720p a 1 Mbps.
 * Uma tela 1920×1080 era reduzida para 1280×720 no apresentador e ampliada de volta no
 * viewer (blur em texto/planilha), e 1 Mbps fica abaixo do preset LiveKit para 720p
 * (1,5 Mbps). O orçamento de uplink liberado pelo cap da câmera durante a tela
 * (~300 kbps) e pelo áudio a 32 kbps cobre a diferença. O SDK ≥ 2.20 aplica
 * `degradationPreference: 'maintain-resolution'` à tela: sob pressão de banda o Chrome
 * reduz fps, não a nitidez.
 */
export type ScreenShareQualityProfile = 'normal' | 'high';

export const SCREEN_SHARE_QUALITY_STORAGE_KEY = 'evolutto-meet-screen-quality';
export const SCREEN_SHARE_QUALITY_CHANGED_EVENT = 'evolumeet:screen-quality-changed';

export type ScreenShareAdaptMode = 'default' | 'economy' | 'poor' | 'large-room';

export type ScreenSharePublishDefaults = Pick<
  TrackPublishDefaults,
  'screenShareEncoding' | 'screenShareSimulcastLayers'
>;

export type ScreenShareRoomOptions = {
  capture: ScreenShareCaptureOptions;
  publish: ScreenSharePublishDefaults;
};

const PROFILE_LABELS: Record<ScreenShareQualityProfile, string> = {
  normal: 'Normal',
  high: 'Alta',
};

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export const SCREEN_SHARE_NORMAL_MAX_HEIGHT = 1080;
export const SCREEN_SHARE_NORMAL_MAX_BITRATE = 1_500_000;
export const SCREEN_SHARE_HIGH_MAX_BITRATE = 2_500_000;

/** Altura máxima default do perfil Normal (env `NEXT_PUBLIC_SCREEN_SHARE_MAX_HEIGHT`). */
export function getScreenShareMaxHeightEnv(): number {
  return parsePositiveInt(
    process.env.NEXT_PUBLIC_SCREEN_SHARE_MAX_HEIGHT,
    SCREEN_SHARE_NORMAL_MAX_HEIGHT,
  );
}

/** Bitrate máximo default do perfil Normal em bps (env `NEXT_PUBLIC_SCREEN_SHARE_MAX_BITRATE`). */
export function getScreenShareMaxBitrateEnv(): number {
  return parsePositiveInt(
    process.env.NEXT_PUBLIC_SCREEN_SHARE_MAX_BITRATE,
    SCREEN_SHARE_NORMAL_MAX_BITRATE,
  );
}

/**
 * Captura 1080 permitida (env `NEXT_PUBLIC_SCREEN_SHARE_ALLOW_1080`). Default true.
 * `false` rebaixa Normal e Alta para o teto 720 (kill-switch operacional).
 */
export function isScreenShareHighAllowed(): boolean {
  const raw = process.env.NEXT_PUBLIC_SCREEN_SHARE_ALLOW_1080?.trim().toLowerCase();
  if (raw === '0' || raw === 'false' || raw === 'no') return false;
  return true;
}

export function isScreenShareQualityProfile(
  value: unknown,
): value is ScreenShareQualityProfile {
  return value === 'normal' || value === 'high';
}

export function getDefaultScreenShareQualityProfile(): ScreenShareQualityProfile {
  return 'normal';
}

export function getScreenShareQualityLabel(profile: ScreenShareQualityProfile): string {
  return PROFILE_LABELS[profile];
}

export function readScreenShareQualityProfile(): ScreenShareQualityProfile {
  if (typeof window === 'undefined') {
    return getDefaultScreenShareQualityProfile();
  }
  try {
    const stored = window.localStorage.getItem(SCREEN_SHARE_QUALITY_STORAGE_KEY);
    if (isScreenShareQualityProfile(stored)) {
      if (stored === 'high' && !isScreenShareHighAllowed()) {
        return 'normal';
      }
      return stored;
    }
  } catch {
    // ignore storage errors
  }
  return getDefaultScreenShareQualityProfile();
}

export function writeScreenShareQualityProfile(profile: ScreenShareQualityProfile): void {
  if (typeof window === 'undefined') return;
  const effective =
    profile === 'high' && !isScreenShareHighAllowed() ? 'normal' : profile;
  window.localStorage.setItem(SCREEN_SHARE_QUALITY_STORAGE_KEY, effective);
  window.dispatchEvent(
    new CustomEvent(SCREEN_SHARE_QUALITY_CHANGED_EVENT, {
      detail: { profile: effective },
    }),
  );
}

/** Perfil efetivo sob economia: sempre Normal (nunca Alta). */
export function getEffectiveScreenShareQualityProfile(
  stored: ScreenShareQualityProfile,
  economyEnabled: boolean,
): ScreenShareQualityProfile {
  if (economyEnabled) return 'normal';
  if (stored === 'high' && !isScreenShareHighAllowed()) return 'normal';
  return stored;
}

function buildResolution(width: number, height: number, frameRate: number): VideoResolution {
  return {
    width,
    height,
    frameRate,
    aspectRatio: width / height,
  };
}

function resolutionForProfile(
  profile: ScreenShareQualityProfile,
  mode: ScreenShareAdaptMode,
): VideoResolution {
  const envMaxH = getScreenShareMaxHeightEnv();

  if (mode === 'poor' || mode === 'economy') {
    // 960×540 @ 8–12 fps — congestão / economia
    const fps = mode === 'poor' ? 8 : 12;
    return buildResolution(960, 540, fps);
  }

  if (mode === 'large-room') {
    return buildResolution(1280, Math.min(720, envMaxH), 12);
  }

  // default
  if (profile === 'high' && isScreenShareHighAllowed()) {
    return buildResolution(1920, 1080, 15);
  }

  // Normal: até 1080p (o browser não amplia telas menores; só evita downscale de Full HD).
  // NEXT_PUBLIC_SCREEN_SHARE_ALLOW_1080=false é o kill-switch para voltar ao teto 720.
  const normalCap = isScreenShareHighAllowed() ? SCREEN_SHARE_NORMAL_MAX_HEIGHT : 720;
  const height = Math.min(normalCap, envMaxH);
  const width = Math.round((height * 16) / 9);
  return buildResolution(width, height, 15);
}

function encodingForProfile(
  profile: ScreenShareQualityProfile,
  mode: ScreenShareAdaptMode,
): VideoEncoding {
  const envMaxBitrate = getScreenShareMaxBitrateEnv();

  if (mode === 'poor') {
    return { maxBitrate: 600_000, maxFramerate: 8 };
  }
  if (mode === 'economy') {
    return { maxBitrate: Math.min(800_000, envMaxBitrate), maxFramerate: 12 };
  }
  if (mode === 'large-room') {
    return { maxBitrate: Math.min(800_000, envMaxBitrate), maxFramerate: 12 };
  }

  if (profile === 'high' && isScreenShareHighAllowed()) {
    return {
      maxBitrate: SCREEN_SHARE_HIGH_MAX_BITRATE,
      maxFramerate: 15,
    };
  }

  return {
    maxBitrate: Math.min(SCREEN_SHARE_NORMAL_MAX_BITRATE, envMaxBitrate),
    maxFramerate: 15,
  };
}

/**
 * Opções de captura para getDisplayMedia / setScreenShareEnabled.
 * Default ≤ 1080p@15 (`ideal`, sem upscale de telas menores).
 *
 * `cursor: 'always'` garante que o cursor do apresentador seja visível a todo momento —
 * sem isso o Chrome pode ocultar o cursor quando ele sai da área compartilhada.
 * O campo fica em `video` (constraints estendidos do getDisplayMedia) pois não existe
 * em MediaTrackConstraints padrão; TypeScript exige o cast.
 */
export function getScreenShareCaptureOptions(
  profile: ScreenShareQualityProfile = readScreenShareQualityProfile(),
  mode: ScreenShareAdaptMode = 'default',
): ScreenShareCaptureOptions {
  const resolution = resolutionForProfile(profile, mode);
  // LiveKit repassa `video` ao getDisplayMedia; o tipo TS só declara displaySurface,
  // mas o Chrome aceita cursor:'always' em runtime — cast no objeto inteiro.
  return {
    audio: true,
    selfBrowserSurface: 'include',
    resolution,
    contentHint: 'detail',
    video: { cursor: 'always' },
  } as ScreenShareCaptureOptions;
}

/**
 * Camada de fallback (640×360 @ 5 fps / 300 kbps) — **desligada por padrão** desde set/2026.
 *
 * Histórico: foi adicionada para viewers com conexão ruim, mas no apresentador ela custa
 * um 2º encoder de tela + ~300 kbps de uplink e, principalmente, declara 2 camadas ao SFU:
 * quando o Chrome desliga a camada alta por banda/CPU o score do publisher cai um nível
 * (`DistanceToDesired`). Recomendação LiveKit para tela: **uma única camada** (temporal
 * scalability do VP8 já dá ao SFU margem para viewers lentos; `adaptiveStream` pausa/retoma).
 * `NEXT_PUBLIC_SCREEN_SHARE_FALLBACK_LAYER=1` reativa para A/B.
 */
const SCREEN_SHARE_FALLBACK_LAYER = new VideoPreset(640, 360, 300_000, 5);

export function isScreenShareFallbackLayerEnabled(): boolean {
  return process.env.NEXT_PUBLIC_SCREEN_SHARE_FALLBACK_LAYER?.trim() === '1';
}

/**
 * Defaults de publish: encoding de camada única (screenShareSimulcastLayers vazio).
 * Fallback só com flag explícita e fora de `poor`/`economy`.
 */
export function getScreenSharePublishDefaults(
  profile: ScreenShareQualityProfile = readScreenShareQualityProfile(),
  mode: ScreenShareAdaptMode = 'default',
): ScreenSharePublishDefaults {
  const addFallbackLayer =
    isScreenShareFallbackLayerEnabled() && mode !== 'poor' && mode !== 'economy';
  return {
    screenShareEncoding: encodingForProfile(profile, mode),
    screenShareSimulcastLayers: addFallbackLayer ? [SCREEN_SHARE_FALLBACK_LAYER] : [],
  };
}

export function getScreenShareRoomOptions(
  profile: ScreenShareQualityProfile = readScreenShareQualityProfile(),
  mode: ScreenShareAdaptMode = 'default',
): ScreenShareRoomOptions {
  return {
    capture: getScreenShareCaptureOptions(profile, mode),
    publish: getScreenSharePublishDefaults(profile, mode),
  };
}

/** Constraints para applyConstraints em track ScreenShare já publicado. */
export function getScreenShareMediaConstraints(
  profile: ScreenShareQualityProfile = readScreenShareQualityProfile(),
  mode: ScreenShareAdaptMode = 'default',
): MediaTrackConstraints {
  const resolution = resolutionForProfile(profile, mode);
  return {
    width: { ideal: resolution.width, max: resolution.width },
    height: { ideal: resolution.height, max: resolution.height },
    frameRate: { max: resolution.frameRate ?? 15 },
  };
}

/** Aceite: Normal nunca pede captura acima de 1080 (sem 1440p/4K no uplink). */
export function assertNormalCaptureIsAtMost1080(
  capture: ScreenShareCaptureOptions,
): boolean {
  const h = capture.resolution?.height ?? 0;
  return h > 0 && h <= SCREEN_SHARE_NORMAL_MAX_HEIGHT;
}
